import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function HeroSlider({ slides, currentPage }) {
  const { t, i18n } = useTranslation('home');
  const isRTL = i18n.language === 'ar';
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-slide
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  return (
    <section className="relative h-screen overflow-hidden bg-slate-900">
      {/* Header integrated into slider */}
      <header className="absolute top-0 left-0 right-0 z-50 bg-transparent">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Logo */}
            <Link to="/" className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <img 
                src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
                alt="FaheemGPT" 
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-white text-xl font-bold tracking-tight">FaheemGPT</span>
            </Link>
            
            {/* Navigation */}
            <nav className={`hidden md:flex items-center gap-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Link to="/about" className={`${currentPage === 'about' ? 'text-white' : 'text-white/80'} hover:text-white transition-colors font-medium`}>{t('nav.about')}</Link>
              <Link to="/meet-founder" className={`${currentPage === 'meet-founder' ? 'text-white' : 'text-white/80'} hover:text-white transition-colors font-medium`}>{t('nav.meetFounder', 'Meet Founder')}</Link>
              <Link to="/partners" className={`${currentPage === 'partners' ? 'text-white' : 'text-white/80'} hover:text-white transition-colors font-medium`}>{t('nav.partners')}</Link>
              <Link to="/how-it-works" className={`${currentPage === 'how-it-works' ? 'text-white' : 'text-white/80'} hover:text-white transition-colors font-medium`}>{t('nav.howItWorks')}</Link>
              <Link to="/login" className="text-blue-400 hover:text-blue-300 transition-colors font-medium">{t('nav.login', 'Login')}</Link>
            </nav>

            {/* Language Switcher */}
            <button 
              onClick={() => i18n.changeLanguage(isRTL ? 'en' : 'ar')}
              className="text-white/80 hover:text-white transition-colors text-sm font-medium"
            >
              {isRTL ? 'EN' : 'العربية'}
            </button>
          </div>
        </div>
      </header>

      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className="absolute inset-0"
          style={{
            opacity: index === currentSlide ? 1 : 0,
            visibility: index === currentSlide ? 'visible' : 'hidden',
            transition: 'opacity 800ms ease-in-out, visibility 800ms ease-in-out',
            zIndex: index === currentSlide ? 10 : 0
          }}
        >
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${slide.image})` }}
          >
            <div className="absolute inset-0 bg-slate-900/70"></div>
          </div>
          
          {/* Content - Centered */}
          <div className="relative h-full flex items-center justify-center">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
                {slide.title}
                <span className="block text-blue-400 mt-2">{slide.highlight}</span>
              </h1>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed max-w-2xl mx-auto">
                {slide.description}
              </p>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <button
        onClick={isRTL ? nextSlide : prevSlide}
        className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-6' : 'left-6'} z-20 w-14 h-14 rounded-full border-2 border-white/30 hover:border-white/60 flex items-center justify-center text-white/60 hover:text-white transition-all`}
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={isRTL ? prevSlide : nextSlide}
        className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-6' : 'right-6'} z-20 w-14 h-14 rounded-full border-2 border-white/30 hover:border-white/60 flex items-center justify-center text-white/60 hover:text-white transition-all`}
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentSlide ? 'bg-white w-8' : 'bg-white/40 hover:bg-white/60'
            }`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-8 z-20">
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-white/60 rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}
